import { produce } from "immer";
import { keyBy, memoize, remove } from "lodash-es";
import { uniq } from "ramda";

import {
  ANNOTATION_TYPE,
  CHART_COLOR_SET,
  CHART_TYPE,
  DATA_VIEWER_MODE,
  DEFAULT_DATAVIEWER_SYMBOL_SIZE,
  ENTITY_TYPE,
  FEATURE_TABLE_DEFAULT_HEIGHT,
  FEATURE_TABLE_DEFAULT_WIDTH,
  UNSPECIFIED_ENTITY_ID,
  VIEW,
} from "@/settings/settings.ts";
import type { IConditionCombination, ISetChartData } from "@/ui-modules/analytical-viewer/DatasetChart.tsx";

import type WebApiClient from "../../api/WebApiClient.js";
import { type IDownloadGDAFileParams } from "../../api/WebApiClient.js";
import { createAnnotationLookupByKey } from "../../app-utils/chart.js";
import { getDataSeriesKey, parseDurationAsSeconds } from "../../app-utils/chart-worker.js";
import { chooseUniqueColor, createDiscreteColorGradient } from "../../app-utils/color.js";
import { createErrorHandler } from "../../app-utils/errors.js";
import { pushRoute } from "../../app-utils/route.js";
import { cloneSelectionModel, emptySelectionModel, type ISelectionModel } from "../../app-utils/selection-model.js";
import { type Dictionary } from "../../app-utils/type.js";
import {
  type ChartLayoutState,
  type ColumnAnnotation,
  type ConditionFilter,
  type DataSeries,
  type DataseriesProperties,
  type Dataset,
  type DatasetColumn,
  type DatasetViewState,
  type Group,
  type InputTableQuerySpec,
  type SelectedColumnAnnotation,
  type SplitPaneLayoutState,
  type StringMapEntry,
  type TableQuerySpec,
  type View,
} from "../../graphql.js";
import {
  ADD_DATA_SERIES_FAILURE,
  ADD_DATA_SERIES_REQUEST,
  ADD_DATA_SERIES_SUCCESS,
  CHART_COLOR_SETTINGS_CHANGED,
  CHART_FILTERED_CONDITIONS_CHANGED,
  CHART_LAYOUT_STATE_CHANGED,
  CHART_SELECTED_CONDITIONS_CHANGED,
  CHART_SELECTED_DATASERIES_CHANGED,
  CHART_SELECTION_CHANGED,
  CHART_TABLE_SELECTION_CHANGED,
  CLEAR_DATASET_VIEWER,
  LOAD_AVAILABLE_DATASETS_FAILURE,
  LOAD_AVAILABLE_DATASETS_REQUEST,
  LOAD_AVAILABLE_DATASETS_SUCCESS,
  LOAD_DATA_SERIES_FAILURE,
  LOAD_DATA_SERIES_REQUEST,
  LOAD_DATA_SERIES_SUCCESS,
  LOAD_DATASET_COLUMNS_MIN_MAX_VALUES_FAILURE,
  LOAD_DATASET_COLUMNS_MIN_MAX_VALUES_REQUEST,
  LOAD_DATASET_COLUMNS_MIN_MAX_VALUES_SUCCESS,
  LOAD_DATASET_FAILURE,
  LOAD_DATASET_REQUEST,
  LOAD_DATASET_SUCCESS,
  LOAD_DEFAULT_DATASET_FAILURE,
  LOAD_DEFAULT_DATASET_REQUEST,
  LOAD_DEFAULT_DATASET_SUCCESS,
  LOAD_GROUP_DATA_SERIES_FAILURE,
  LOAD_GROUP_DATA_SERIES_REQUEST,
  LOAD_GROUP_DATA_SERIES_SUCCESS,
  LOAD_GROUP_DATASET_FAILURE,
  LOAD_GROUP_DATASET_REQUEST,
  LOAD_GROUP_DATASET_SUCCESS,
  LOAD_VIEW_SUCCESS,
  RECALCULATE_DATA_SERIES_COLORS,
  SEARCH_DATA_SERIES_FAILURE,
  SEARCH_DATA_SERIES_REQUEST,
  SEARCH_DATA_SERIES_SUCCESS,
  SEARCH_GROUP_DATA_SERIES_FAILURE,
  SEARCH_GROUP_DATA_SERIES_REQUEST,
  SEARCH_GROUP_DATA_SERIES_SUCCESS,
  SET_CHART_DATA,
  SPLIT_BY_CONDITION,
  SPLIT_PANE_LAYOUT_STATE_CHANGED,
  TOGGLE_DISPLAY_OF_DATA_SERIES_WITHOUT_DATA,
  TOGGLE_FILTER_PANEL,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { shouldLoadGroup } from "./saved-group.js";
import {
  createSelectionSpecFromTableViewState,
  createTableSpecForGroupParent,
  getTableDataSlice,
  type ITableData,
  sameTableSpec,
} from "./table-data.js";

/**
 * The general mode of action of the dataset viewerr is as follows...
 * TODO: add high-level code description of dataset viewer
 */

export const SAMPLES_ANNOTATION: ColumnAnnotation = {
  name: "Samples",
  field: "columns",
  type: "categorical",
  values: [],
  valueSet: [],
};

/**
 * State definition
 */

export type IDataSeriesColorMap = Record<string, string>; // map by dataSeriesName

export interface IColorSettings {
  presetColors?: string[]; // color definition for the color selector
  rowColors?: IDataSeriesColorMap; // map from row name to color (a hex rgb string)
}

export type IColumnAnnotationLookup = Record<string, StringMapEntry[]>; // map by column key ; // with multi values, it is possible that multiple annotations get the same key, therefore the lookup targets a list

export interface ICustomDatasetColumn extends DatasetColumn {
  annotationsLookup?: IColumnAnnotationLookup;
}

export interface IDatasetViewerState {
  dataset: Dataset; // the currently selected dataset
  isLoading: boolean; // true if the dataset is currently being loaded
  isLoadingDataSeries: boolean; // true if the data series are currently being loaded/searched
  availableAnnotations: ColumnAnnotation[]; // the list of all conditions PLUS the special 'Columns' condition
  filteredOutAnnotations: IColumnAnnotationFilterMap;
  filteredOutColumns: string[]; // the filtered out annotations might affect directly the listing of columns
  filteredOutValueIndicesByColumn: Dictionary<string[]>; //  some multi value annotations might be filtered out in which case all the annotations sharing the same index will be filtered out as well
  selectedAnnotations: ColumnAnnotation[]; // list of all SELECTED conditions in the data-viewer
  searchedDataSeriesNames: string[]; // Name list of available dataseries filtered by a search term
  selectedDataSeries: SelectedDataSeries[]; // name, color and split condition of selected data series
  dataSeriesMap: IDataSeriesMap; // the actual data values from the GDA file, keyed by data series name
  isLoadingAvailableDatasets: boolean;
  availableDatasets: Dataset[]; // the full set of available datasets
  colorSettings: IColorSettings; // Collects all data for color assignment to data series
  chartLayout: ChartLayoutState; // wrapper for all layouting props
  showFilterPanel: boolean; // true if the filter panel is visible
  mode: number; // either dataset or sample mode, controlling mainly the ChartSettings panel
  splitPaneLayout: SplitPaneLayoutState;
  chartSelection: string[]; // column identifiers for user selection via the chart, shrinking the universe of the chart table
  chartTableSelectionModel: ISelectionModel; // selection model of the chart side table
  chartData: IConditionCombination[];
  isComputingChartData: boolean;
  allDataSeriesKeysWithData: string[];
  hideEmptyDataSeries: boolean;

  tableSelectionSpec?: InputTableQuerySpec; // corresponding to the table spec of the table from where the groupId comes from
}

/**
 * Interfaces
 */
export type IDataSeriesMap = Record<string, any>;

export type IColumnAnnotationFilterMap = Record<string, IColumnAnnotationFilter>;

// same as IConditionFilter but with optional parameters
export interface IColumnAnnotationFilter {
  name: string;
  categoricalValues?: string[];
  minNumericalValue?: number;
  maxNumericalValue?: number;
}

export interface SelectedDataSeries extends DataseriesProperties {
  isLoading: boolean;
}

export interface IDuration {
  value: number;
  unit?: string;
}

/**
 * Initial data and constants. Note that the first color (black) replaced with the currently selected color on-the-fly
 */
export const defaultColorPalette: string[] = [
  "#000000",
  "#5e4fa2",
  "#3288bd",
  "#66c2a5",
  "#abdda4",
  "#e6f598",
  "#fee08b",
  "#fdae61",
  "#f46d43",
  "#d53e4f",
  "#9e0142",
  "#305e97",
  "#2988cc",
  "#42affa",
  "#4A4A4A",
  "#9B9B9B",
  "#dddddd",
];

const initialState: IDatasetViewerState = {
  dataset: null,
  isLoading: false,
  isLoadingDataSeries: false,
  selectedAnnotations: [SAMPLES_ANNOTATION],
  filteredOutAnnotations: {},
  filteredOutColumns: [],
  filteredOutValueIndicesByColumn: {},
  selectedDataSeries: [],
  searchedDataSeriesNames: [],
  isLoadingAvailableDatasets: false,
  availableDatasets: [],
  availableAnnotations: [SAMPLES_ANNOTATION],
  dataSeriesMap: {},
  colorSettings: {
    presetColors: defaultColorPalette.slice(0),
    rowColors: {},
  },
  chartLayout: {
    chartType: CHART_TYPE.BAR,
    isLogScale: false,
    timeSeries: false,
    errorBars: true,
    filterOutEmptyValues: false,
    symbolSize: DEFAULT_DATAVIEWER_SYMBOL_SIZE,
  },
  showFilterPanel: false,
  mode: DATA_VIEWER_MODE.DATASET,
  splitPaneLayout: {
    hidden: true,
    size: FEATURE_TABLE_DEFAULT_WIDTH, // we should change the name of this property to featuresTableSize, but need to change the database storage as well
    verticalLayout: true,
  },
  chartSelection: [],
  chartTableSelectionModel: cloneSelectionModel(emptySelectionModel),
  chartData: [],
  isComputingChartData: false,
  allDataSeriesKeysWithData: [],
  hideEmptyDataSeries: true, // filter out split data series without data by default
  tableSelectionSpec: null,
};

const initialSelectedDataSeries: SelectedDataSeries = {
  isLoading: false,
  color: null,
  hidden: false,
  name: null,
  splitByCondition: null,
  splitParent: null,
  splitValue: null,
  splitMultiValueIndex: null,
};

/**
 * Reducers
 */

export default createReducer(initialState, {
  [LOAD_DATASET_REQUEST]: (state: IDatasetViewerState): IDatasetViewerState => {
    const initialColorSettings: IColorSettings = initialState.colorSettings;
    return {
      ...initialState,
      // we set directly from initialState ! so just pick the part that needs to be preserved
      isLoading: true,
      mode: DATA_VIEWER_MODE.DATASET,
      colorSettings: {
        presetColors: initialColorSettings.presetColors?.slice(0),
        rowColors: {},
      },
      availableDatasets: state.availableDatasets,
    };
  },

  [LOAD_DATASET_SUCCESS]: (state: IDatasetViewerState, dataset: Dataset): IDatasetViewerState => {
    let availableAnnotations: ColumnAnnotation[] = [];
    const selectedAnnotations: ColumnAnnotation[] = [];
    const customDataset: Dataset = dataset && { ...dataset };

    // dataset might be null, for example when fetching the default dataset for a specific genome id
    if (customDataset) {
      // Add the the columns as a special condition to the beginning of the available annotations list
      availableAnnotations = createAvailableColumnAnnotations(customDataset);
      // add the SAMPLES_ANNOTATION  by default (with the related props as calculated in the above method)
      selectedAnnotations.push(
        availableAnnotations.find((annotation) => annotation.field === SAMPLES_ANNOTATION.field),
      );
    }

    // create a lookup/index for each column in order to fasten the chart data manipulation
    if (customDataset) {
      customDataset.columns = customDataset.columns.map((column: DatasetColumn) => {
        return { ...column, annotationsLookup: createAnnotationLookupByKey(column) };
      });
    }

    const chartLayout: ChartLayoutState = updateTimeSeriesLayoutOptionFromSelectedAnnotations(
      selectedAnnotations,
      availableAnnotations,
      state.chartLayout,
    );

    return {
      ...state,
      isLoading: false,
      dataset: customDataset,
      availableAnnotations,
      selectedAnnotations,
      chartLayout,
    };
  },

  [LOAD_GROUP_DATASET_REQUEST]: (
    state: IDatasetViewerState,
    tableSelectionSpec: InputTableQuerySpec,
  ): IDatasetViewerState => {
    const initialColorSettings: IColorSettings = initialState.colorSettings;
    return {
      ...initialState,
      // we set directly from initialState ! so just pick the part that needs to be preserved
      isLoading: true,
      mode: DATA_VIEWER_MODE.GROUP,
      colorSettings: {
        presetColors: initialColorSettings.presetColors?.slice(0),
        rowColors: {},
      },
      tableSelectionSpec: tableSelectionSpec,
      availableDatasets: state.availableDatasets,
    };
  },

  [LOAD_GROUP_DATASET_SUCCESS]: (state: IDatasetViewerState, dataset: Dataset): IDatasetViewerState => {
    let availableAnnotations: ColumnAnnotation[] = [];
    const selectedAnnotations: ColumnAnnotation[] = [];
    const customDataset: Dataset = dataset && { ...dataset };

    // dataset might be null, for example when fetching the default dataset for a specific genome id
    if (dataset) {
      // Add the the columns as a special condition to the beginning of the available annotations list
      availableAnnotations = createAvailableColumnAnnotations(dataset);
      // add the SAMPLES_ANNOTATION  by default (with the related props as calculated in the above method)
      selectedAnnotations.push(
        availableAnnotations.find((annotation) => annotation.field === SAMPLES_ANNOTATION.field),
      );
    }

    // create a lookup/index for each column in order to fasten the chart data manipulation
    if (customDataset) {
      customDataset.columns = customDataset.columns.map((column: DatasetColumn) => {
        return { ...column, annotationsLookup: createAnnotationLookupByKey(column) };
      });
    }

    const chartLayout: ChartLayoutState = updateTimeSeriesLayoutOptionFromSelectedAnnotations(
      selectedAnnotations,
      availableAnnotations,
      state.chartLayout,
    );

    return {
      ...state,
      isLoading: false,
      dataset: customDataset,
      availableAnnotations,
      selectedAnnotations,
      chartLayout,
    };
  },

  [ADD_DATA_SERIES_REQUEST]: (state: IDatasetViewerState, newSeriesName: string): IDatasetViewerState => {
    return singleDataSeriesRequested(state, newSeriesName);
  },

  [ADD_DATA_SERIES_SUCCESS]: (state: IDatasetViewerState, newSeries: DataSeries): IDatasetViewerState => {
    return dataSeriesAdded(state, [newSeries]);
  },

  [CLEAR_DATASET_VIEWER]: (): IDatasetViewerState => {
    return initialState;
  },

  [LOAD_DATA_SERIES_REQUEST]: (state: IDatasetViewerState, dataSeriesNames: string[]): IDatasetViewerState => {
    return multipleDataSeriesRequested(state, dataSeriesNames);
  },

  [LOAD_DATA_SERIES_SUCCESS]: (state: IDatasetViewerState, dataSeriesList: DataSeries[]): IDatasetViewerState => {
    // Add the data series but remove any data series that not part of the data series list from the server
    // This behaviour is needed because if the user changes the data set we want to keep as many data series as
    // possible and throw out the ones that could not be found in the newly selected data set
    return dataSeriesAdded(state, dataSeriesList, true);
  },

  [LOAD_GROUP_DATA_SERIES_REQUEST]: (state: IDatasetViewerState, dataSeriesNames: string[]): IDatasetViewerState => {
    return multipleDataSeriesRequested(state, dataSeriesNames);
  },

  [LOAD_GROUP_DATA_SERIES_SUCCESS]: (state: IDatasetViewerState, dataSeriesList: DataSeries[]): IDatasetViewerState => {
    // Add the data series but remove any data series that not part of the data series list from the server
    // This behaviour is needed because if the user changes the data set we want to keep as many data series as
    // possible and throw out the ones that could not be found in the newly selected data set
    return dataSeriesAdded(state, dataSeriesList, true);
  },

  [LOAD_DEFAULT_DATASET_REQUEST]: (state: IDatasetViewerState): IDatasetViewerState => {
    return {
      ...state,
      isLoading: true,
    };
  },
  [LOAD_DEFAULT_DATASET_SUCCESS]: (state: IDatasetViewerState): IDatasetViewerState => {
    return {
      ...state,
      isLoading: false,
    };
  },
  [LOAD_DEFAULT_DATASET_FAILURE]: (state: IDatasetViewerState): IDatasetViewerState => {
    return {
      ...state,
      isLoading: false,
    };
  },

  [LOAD_DATASET_FAILURE]: (state: IDatasetViewerState): IDatasetViewerState => {
    return {
      ...state,
      isLoading: false,
      dataset: null,
    };
  },

  [LOAD_AVAILABLE_DATASETS_REQUEST]: (state: IDatasetViewerState): IDatasetViewerState => {
    return {
      ...state,
      isLoadingAvailableDatasets: true,
    };
  },

  [LOAD_AVAILABLE_DATASETS_SUCCESS]: (state: IDatasetViewerState, datasets: Dataset[]): IDatasetViewerState => {
    return {
      ...state,
      availableDatasets: datasets,
      isLoadingAvailableDatasets: false,
    };
  },

  [LOAD_AVAILABLE_DATASETS_FAILURE]: (state: IDatasetViewerState): IDatasetViewerState => {
    return {
      ...state,
      availableDatasets: [],
      isLoadingAvailableDatasets: false,
    };
  },

  [CHART_LAYOUT_STATE_CHANGED]: (state: IDatasetViewerState, payload: ChartLayoutState): IDatasetViewerState => {
    return { ...state, chartLayout: { ...state.chartLayout, ...payload } };
  },

  [CHART_COLOR_SETTINGS_CHANGED]: (state: IDatasetViewerState, payload: IColorSettings): IDatasetViewerState => {
    return { ...state, colorSettings: { ...state.colorSettings, ...payload } };
  },

  [CHART_SELECTED_CONDITIONS_CHANGED]: (
    state: IDatasetViewerState,
    payload: ColumnAnnotation[],
  ): IDatasetViewerState => {
    const chartLayout: ChartLayoutState = updateTimeSeriesLayoutOptionFromSelectedAnnotations(
      payload,
      state.availableAnnotations,
      state.chartLayout,
    );

    return {
      ...state,
      selectedAnnotations: payload,
      chartLayout: chartLayout,
    };
  },

  [CHART_FILTERED_CONDITIONS_CHANGED]: (
    state: IDatasetViewerState,
    payload: { conditionField: string; conditionFilter: IColumnAnnotationFilter },
  ): IDatasetViewerState => {
    const conditionField: string = payload.conditionField;
    const conditionFilter: IColumnAnnotationFilter = payload.conditionFilter;

    // set the new filter property
    const filteredAnnotations: IColumnAnnotationFilterMap = produce(state.filteredOutAnnotations, (draft) => {
      draft[conditionField] = conditionFilter;
    });
    // store the columns and valueIndices to be hidden
    const { filteredOutColumns, filteredOutValueIndicesByColumn } = filteredOutColumnsAndValueIndices(
      state.dataset,
      filteredAnnotations,
    );
    return {
      ...state,
      filteredOutAnnotations: filteredAnnotations,
      filteredOutColumns,
      filteredOutValueIndicesByColumn,
    };
  },

  [CHART_SELECTED_DATASERIES_CHANGED]: (
    state: IDatasetViewerState,
    payload: SelectedDataSeries[],
  ): IDatasetViewerState => {
    // Gets the just the basic attributes of data sets. These
    return { ...state, selectedDataSeries: payload };
  },
  [SPLIT_BY_CONDITION]: (
    state: IDatasetViewerState,
    payload: { dataSeries: SelectedDataSeries; splitByCondition: ColumnAnnotation | null },
  ): IDatasetViewerState => {
    const splitDataSeries: SelectedDataSeries = payload.dataSeries;
    const splitByCondition: ColumnAnnotation | null = payload.splitByCondition;

    /*
      state.selectedDataSeries contains all data series that are currently part of the chart (visible or invisible).
      If a data series has already been split, state.selectedDataSeries holds entries for
      - the parent data series plus
      - n entries for the number of data series that result from the split operation
        (these series are indented below the parent in the side-panel)
        -> the 'split series' have special fields:
              {...
                 splitByCondition  : "Time3"
                 splitParent       : "PsAffx.psZS001xP06f"
                 splitValue        : "48 hr"
              }
     Note, the splitByCondition field is also set in the parent series.
     The immutable selectedDataSeries object of the state must be cloned so that it can be changed.
    */
    let updatedSelectedDataSeries: SelectedDataSeries[] = state.selectedDataSeries.slice(0);

    // remove existing split series of a previous split operation, if any, but keep the parent series
    if (splitByCondition === null || splitDataSeries.splitByCondition) {
      remove(updatedSelectedDataSeries, (ds: SelectedDataSeries) => ds.splitParent === splitDataSeries.name);
      // needed to set to null the parent? (overridden below)
    }

    // create the new split series
    if (splitByCondition) {
      const discreteColorGradient: string[] = createDiscreteColorGradient(
        splitDataSeries.color,
        splitByCondition.valueSet.length,
      );

      const splitSeries: SelectedDataSeries[] = splitByCondition.valueSet.map(
        (splitGroupName: string, seriesIndex: number) => {
          const splitSeriesColor: string = discreteColorGradient[seriesIndex];
          const splitSeriesName = `${splitDataSeries.name} ${splitByCondition.name} ${splitGroupName}`;
          // infer the valueIndex of this split entry, because valueSet does not provide the valueIndex information
          const splitSeriesMultiValueIndex: string = splitByCondition.values.find(
            (entry) => entry.value == splitGroupName,
          )?.multiValueIndex;
          return {
            isLoading: false,
            name: splitSeriesName,
            color: splitSeriesColor,
            hidden: false,
            splitValue: splitGroupName,
            splitParent: splitDataSeries.name,
            splitByCondition: splitByCondition.field,
            splitMultiValueIndex: splitSeriesMultiValueIndex,
          };
        },
      );
      // append the split series to updatedSelectedDataSeries
      // the parent- and split-series can be ordered arbitrarily in this array
      updatedSelectedDataSeries = updatedSelectedDataSeries.concat(splitSeries);
    }

    // update the splitByCondition field of the parent data series
    updatedSelectedDataSeries = updatedSelectedDataSeries.map((d: SelectedDataSeries) => {
      return d.name === splitDataSeries.name ? { ...d, splitByCondition: splitByCondition?.field } : d;
    });

    return { ...state, selectedDataSeries: updatedSelectedDataSeries };
  },

  [SEARCH_DATA_SERIES_REQUEST]: (state: IDatasetViewerState): IDatasetViewerState => {
    return { ...state, isLoadingDataSeries: true };
  },

  [SEARCH_DATA_SERIES_FAILURE]: (state: IDatasetViewerState): IDatasetViewerState => {
    return { ...state, isLoadingDataSeries: false };
  },

  [SEARCH_DATA_SERIES_SUCCESS]: (state: IDatasetViewerState, payload: DataSeries[]): IDatasetViewerState => {
    // Gets just the basic attributes of data sets filtered by a search term for listing the sets in a drop-down
    // We must preserve the list order.
    return { ...state, isLoadingDataSeries: false, searchedDataSeriesNames: payload.map((d) => d.name) };
  },

  [SEARCH_GROUP_DATA_SERIES_REQUEST]: (state: IDatasetViewerState): IDatasetViewerState => {
    return { ...state, isLoadingDataSeries: true };
  },

  [SEARCH_GROUP_DATA_SERIES_FAILURE]: (state: IDatasetViewerState): IDatasetViewerState => {
    return { ...state, isLoadingDataSeries: false };
  },

  [SEARCH_GROUP_DATA_SERIES_SUCCESS]: (state: IDatasetViewerState, payload: DataSeries[]): IDatasetViewerState => {
    // Gets just the basic attributes of data sets filtered by a search term for listing the sets in a drop-down
    // We must preserve the list order.
    return { ...state, isLoadingDataSeries: false, searchedDataSeriesNames: payload.map((d) => d.name) };
  },

  [LOAD_VIEW_SUCCESS]: (state: IDatasetViewerState, payload: View): IDatasetViewerState => {
    if (payload.viewType === VIEW.DATA_VIEW.type && payload.state.datasetView.dataset) {
      // Add the the columns as a special condition to the beginning of the available annotations list
      const dsView: DatasetViewState = payload.state.datasetView;
      const availableAnnotations: ColumnAnnotation[] = createAvailableColumnAnnotations(dsView.dataset);

      // We store the new data series keyed by their name in a map
      const newDataSeriesMap: IDataSeriesMap = keyBy(dsView.dataSeries.slice(), "name");

      // reconstruct the filters
      const columnAnnotationFilter: IColumnAnnotationFilterMap = {};
      if (dsView.filteredOutAnnotations && dsView.filteredOutAnnotations.length > 0) {
        dsView.filteredOutAnnotations.forEach(
          (annotation: ConditionFilter) => (columnAnnotationFilter[annotation.name] = annotation),
        );
      }

      const dataset: Dataset = { ...dsView.dataset };

      // create a lookup/index for each column in order to fasten the chart data manipulation
      if (dataset) {
        dataset.columns = dataset.columns.map((column: DatasetColumn) => {
          return { ...column, annotationsLookup: createAnnotationLookupByKey(column) };
        });
      }

      // filter the columns to be shown
      const { filteredOutColumns, filteredOutValueIndicesByColumn } = filteredOutColumnsAndValueIndices(
        dataset,
        columnAnnotationFilter,
      );

      // construct the list of selected annotations from the list of ISelectedColumnAnnotation objects (including the condition values order)
      const selectedAnnotations: ColumnAnnotation[] = dsView.selectedAnnotations.map(
        (selA: SelectedColumnAnnotation) => {
          let columnAnnotation: ColumnAnnotation = availableAnnotations.find(
            (availA: ColumnAnnotation) => availA.name === selA.label,
          );
          if (selA.conditionValues) {
            // CXR: Columns might contain characters which are incompatible with the DB character set.
            // Therefore we encode them when writing. And decode them here
            columnAnnotation = { ...columnAnnotation, valueSet: selA.conditionValues.map((s) => decodeURI(s)) };
          }
          return columnAnnotation;
        },
      );

      // take care of older saved views without symbolSize: default to 5
      const completedChartLayout: ChartLayoutState = dsView.chartLayout;
      if (completedChartLayout.symbolSize === null) {
        completedChartLayout.symbolSize = DEFAULT_DATAVIEWER_SYMBOL_SIZE;
      }

      return {
        ...state,
        dataset,
        selectedAnnotations,
        selectedDataSeries: dsView.selectedDataSeries?.map((sds) => ({
          ...sds,
          isLoading: false,
        })),
        chartLayout: completedChartLayout,
        splitPaneLayout: dsView.splitPaneLayout || initialState.splitPaneLayout,
        dataSeriesMap: newDataSeriesMap,
        availableAnnotations: availableAnnotations,
        showFilterPanel: dsView.showFilterPanel,
        filteredOutAnnotations: columnAnnotationFilter,
        filteredOutColumns: filteredOutColumns,
        filteredOutValueIndicesByColumn: filteredOutValueIndicesByColumn,
        chartSelection: dsView.chartSelection || initialState.chartSelection,
        chartTableSelectionModel: {
          ...cloneSelectionModel(emptySelectionModel),
          selectedIds: dsView.chartTableSelection || [],
        },
        mode: dsView.groupMode ? DATA_VIEWER_MODE.GROUP : DATA_VIEWER_MODE.DATASET,
        hideEmptyDataSeries: dsView.hideEmptyDataSeries || false,
        tableSelectionSpec: createSelectionSpecFromTableViewState(
          dsView.groupTableView
            ? dsView.groupTableView
            : // we artificially create a tableSelectionSpec if no 'groupTableView' exists (backward compatibility)
              !!dsView.groupMode && {
                tableQuerySpec: createTableSpecForGroupParent(ENTITY_TYPE.SAMPLE, dsView.dataset.id) as TableQuerySpec,
              },
        ),
      };
    } else {
      return state;
    }
  },

  [SPLIT_PANE_LAYOUT_STATE_CHANGED]: (
    state: IDatasetViewerState,
    payload: SplitPaneLayoutState,
  ): IDatasetViewerState => {
    // if we switch the layout mode, we reset the size of the features table to its default
    if (
      (payload.verticalLayout === true || payload.verticalLayout === false) &&
      payload.verticalLayout !== state.splitPaneLayout.verticalLayout
    ) {
      payload.size = payload.verticalLayout ? FEATURE_TABLE_DEFAULT_HEIGHT : FEATURE_TABLE_DEFAULT_WIDTH;
    }
    return { ...state, splitPaneLayout: { ...state.splitPaneLayout, ...payload } };
  },

  [TOGGLE_FILTER_PANEL]: (state: IDatasetViewerState): IDatasetViewerState => {
    return { ...state, showFilterPanel: !state.showFilterPanel };
  },

  [CHART_SELECTION_CHANGED]: (state: IDatasetViewerState, selection: string[]): IDatasetViewerState => {
    return { ...state, chartSelection: selection };
  },

  [CHART_TABLE_SELECTION_CHANGED]: (state: IDatasetViewerState, payload) => {
    return { ...state, chartTableSelectionModel: payload.selectionModel };
  },
  [SET_CHART_DATA]: (state: IDatasetViewerState, payload: ISetChartData): IDatasetViewerState => {
    const allSeriesKeys: string[] = [];
    // if new chart data is provided, calculate a unique list of series key used for hiding the data series selector entries
    if (payload.chartData) {
      payload.chartData.forEach((s: IConditionCombination) => {
        if (s.dataseries) {
          allSeriesKeys.push(...Object.keys(s.dataseries));
        }
      });
    }
    return {
      ...state,
      isComputingChartData:
        payload.isComputingChartData != null ? payload.isComputingChartData : state.isComputingChartData,
      chartData: payload.chartData ? payload.chartData : state.chartData,
      allDataSeriesKeysWithData: payload.chartData ? uniq(allSeriesKeys) : state.allDataSeriesKeysWithData,
    };
  },
  [TOGGLE_DISPLAY_OF_DATA_SERIES_WITHOUT_DATA]: (state: IDatasetViewerState): IDatasetViewerState => {
    return {
      ...state,
      hideEmptyDataSeries: !state.hideEmptyDataSeries,
    };
  },
  [RECALCULATE_DATA_SERIES_COLORS]: (state: IDatasetViewerState): IDatasetViewerState => {
    // This routine adapts the VISIBLE split data series colors in order to optimize their distinction.
    // The hidden series are ignored and left unchanged.
    const selectedDataSeries = state.selectedDataSeries;

    selectedDataSeries
      // for each parent data series which was split...
      .filter((ds) => !ds.splitParent && ds.splitByCondition)
      .forEach((dsp) => {
        // ...get the related VISIBLE split children, calculate the color and assign them to the individual children
        const visibleSplitSeries = selectedDataSeries.filter(
          (ds) => ds.splitParent === dsp.name && isDataSeriesVisible(ds, state),
        );
        // crazy check to satisfy TS - color will always be defined!
        if (dsp.color) {
          const discreteColorGradient: string[] = createDiscreteColorGradient(dsp.color, visibleSplitSeries.length);
          // update the color of the visible split data series
          visibleSplitSeries.forEach((ds, idx) => {
            ds.color = discreteColorGradient[idx];
          });
        }
      });

    return {
      ...state,
      selectedDataSeries: selectedDataSeries,
    };
  },
});

function filteredOutColumnsAndValueIndices(
  dataset: Dataset,
  annotationFilter: IColumnAnnotationFilterMap,
): { filteredOutColumns: string[]; filteredOutValueIndicesByColumn: Dictionary<string[]> } {
  const filteredOutColumns: string[] = [];
  const filteredOutValueIndicesByColumn: Dictionary<string[]> = {};

  // infer the set of filtered columns or filtered indices (e.g. filtered multi values affecting the filtering of complete sub rows)
  dataset.columns.forEach((column: ICustomDatasetColumn) => {
    // special case for the 'Samples' column (keyed as 'columns') because this condition
    //  is not part of the dataset.columns.annotations list
    const samplesColumnAnnotation: StringMapEntry = {
      key: SAMPLES_ANNOTATION.field,
      value: column.name,
      label: null,
      multiValueIndex: null,
    };

    const allColumnAnnotations = [...column.annotations, samplesColumnAnnotation];

    // we gather all annotations that might be affected by the filtering (potentially multiple annotations sharing the same key e.g. multi values)
    const annotationsPotentiallyAffectedByFiltering = allColumnAnnotations.filter(
      (annotation) => annotationFilter[annotation.key],
    );
    // check which annotations are filtered out
    const filteredOutAnnotations = annotationsPotentiallyAffectedByFiltering.filter((filteredAnnotation) =>
      isExcludeColumnByAnnotationFilter(annotationFilter, filteredAnnotation),
    );
    // in case no annotation seems affected then return the full column & related annotations
    if (!filteredOutAnnotations.length || !annotationsPotentiallyAffectedByFiltering.length) {
      // the filtering does not affect this column
      return;
    }
    // collect the valueIndices that must be filtered out (because at least one annotation sharing this valueIndex is excluded)
    const excludedValueIndices = filteredOutAnnotations.map(
      (filteredOutAnnotation) => filteredOutAnnotation.multiValueIndex,
    );
    // if an excluded annotation has no valueIndex it means the full column should be excluded, not only sub rows
    if (
      excludedValueIndices.includes(undefined) ||
      excludedValueIndices.includes(null) ||
      excludedValueIndices.includes("")
    ) {
      filteredOutColumns.push(column.field);
      return;
    }
    // otherwise simple filter out all annotations sharing the excluded valueIndices
    filteredOutValueIndicesByColumn[column.field] = excludedValueIndices;
  });

  return { filteredOutColumns, filteredOutValueIndicesByColumn };
}

/**
 * Action creators
 */

export function toggleFilterPanel() {
  return {
    type: TOGGLE_FILTER_PANEL,
  };
}

export function loadAvailableDatasets(genomeId: number = UNSPECIFIED_ENTITY_ID) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_AVAILABLE_DATASETS_REQUEST });

    return webApiClient
      .fetchAvailableDatasets(genomeId)
      .then((response: Dataset[]) => {
        dispatch({
          type: LOAD_AVAILABLE_DATASETS_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_AVAILABLE_DATASETS_FAILURE,
          alertTitle: "Fetching of Available Datasets Failed",
        }),
      );
  };
}

export function getDefaultDatasetId(genomeId: number = UNSPECIFIED_ENTITY_ID) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<number> => {
    dispatch({ type: LOAD_DEFAULT_DATASET_REQUEST });

    return webApiClient
      .fetchDefaultDatasetId(genomeId)
      .then((defaultDatasetId: number) => {
        dispatch({
          type: LOAD_DEFAULT_DATASET_SUCCESS,
        });
        return defaultDatasetId;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DEFAULT_DATASET_FAILURE,
          alertTitle: "Fetching of Default Dataset ID Failed",
        }),
      );
  };
}

/*
export function getDefaultDatasetId(genomeId: number = UNSPECIFIED_ENTITY_ID, defaultDataSeries?: Array<string>) {
  return (dispatch, getState, webApiClient: WebApiClient) => {

    dispatch({type: LOAD_DATASET_REQUEST});

    return webApiClient.fetchDefaultDatasetId(genomeId)
      .then((response: number) => {
        dispatch({
          type: LOAD_DATASET_SUCCESS,
          payload: response
        });

        // Load the default data series requested by the GeneExpressionView (i.e. ["GAL10"])
        if (response && defaultDataSeries && defaultDataSeries.length > 0) {
          dispatch(loadDataSeries(response.id, defaultDataSeries));
        }
      })
      .catch(createErrorHandler(dispatch,
        {
          actionType: LOAD_DATASET_FAILURE,
          alertTitle: 'Fetching of default dataset failed'
        }));
  };
}
*/

export function clearDatasetViewer() {
  return {
    type: CLEAR_DATASET_VIEWER,
  };
}

export function loadDataset(datasetId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_DATASET_REQUEST });

    return webApiClient
      .fetchDataset(datasetId)
      .then((response: Dataset) => {
        dispatch({
          type: LOAD_DATASET_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DATASET_FAILURE,
          alertTitle: "Fetching of Dataset Failed",
        }),
      );
  };
}

export function loadDatasetMinMaxValues(datasetId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_DATASET_COLUMNS_MIN_MAX_VALUES_REQUEST });

    return webApiClient
      .fetchDatasetMinMaxValues(datasetId)
      .then((response: Dataset) => {
        dispatch({
          type: LOAD_DATASET_COLUMNS_MIN_MAX_VALUES_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DATASET_COLUMNS_MIN_MAX_VALUES_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadDatasetAndDataSeries(datasetId: number, defaultDataSeries?: string[]) {
  return (dispatch, getState, _webApiClient: WebApiClient) => {
    const currentDataSeries: string[] = getState().datasetViewer.selectedDataSeries.map(
      (dataSeries) => dataSeries.name,
    );

    return dispatch(loadDataset(datasetId)).then((response: Dataset) => {
      // Keep the current data series (i.e. ["GAL10"])
      const dataSeriesNames: string[] =
        currentDataSeries.length > 0 ? currentDataSeries : defaultDataSeries ? defaultDataSeries : [];
      if (response && dataSeriesNames.length > 0) {
        dispatch(loadDataSeries(response.id, dataSeriesNames));
      }
    });
  };
}

export function loadGroupDataset(tableSelectionSpec: InputTableQuerySpec) {
  return (dispatch, getState, webApiClient: WebApiClient) => {
    const currentDataSeries: string[] = getState().datasetViewer.selectedDataSeries.map(
      (dataSeries) => dataSeries.name,
    );
    dispatch({
      type: LOAD_GROUP_DATASET_REQUEST,
      payload: tableSelectionSpec,
    });

    return webApiClient
      .fetchGroupDataset(tableSelectionSpec)
      .then((response: Dataset) => {
        dispatch({
          type: LOAD_GROUP_DATASET_SUCCESS,
          payload: response,
        });

        /*
          When loading a dataset in the context of a samples group (or actually any items group)
          we cannot simply switch the dataset (like we can do with standard GDA dataset using the modal dialog),
          therefore loading the previously chosen data series does not make much sense but we still try to, it can be useful.
         */
        if (response && currentDataSeries.length > 0) {
          dispatch(loadGroupDataSeries(currentDataSeries, tableSelectionSpec));
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GROUP_DATASET_FAILURE,
          alertTitle: "Fetching of Sample Group Dataset Failed",
        }),
      );
  };
}

export function loadDataSeries(datasetId: number, dataSeriesNames: string[]) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_DATA_SERIES_REQUEST, payload: dataSeriesNames });

    return webApiClient
      .fetchDataSeries(datasetId, dataSeriesNames)
      .then((response: DataSeries[]) => {
        dispatch({
          type: LOAD_DATA_SERIES_SUCCESS,
          payload: response,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DATA_SERIES_FAILURE,
          alertTitle: "Fetching of Data Series Failed",
        }),
      );
  };
}

export function loadGroupDataSeries(dataSeriesNames: string[], tableQuerySpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_GROUP_DATA_SERIES_REQUEST, payload: dataSeriesNames });

    // currently the data series can only be built from a group, NOT any tableQuerySpec
    return webApiClient
      .fetchGroupDataSeries(dataSeriesNames, tableQuerySpec)
      .then((response: DataSeries[]) => {
        dispatch({
          type: LOAD_GROUP_DATA_SERIES_SUCCESS,
          payload: response,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GROUP_DATA_SERIES_FAILURE,
          alertTitle: "Fetching of Sample Group Data Series Failed",
        }),
      );
  };
}

export function addDataSeries(dataSeriesName: string) {
  return (dispatch, getState, webApiClient: WebApiClient) => {
    const dataset: Dataset = getState().datasetViewer.dataset;

    if (dataset) {
      const commonSplitConditionNameBeforeChange = getCommonSplitByCondition(
        getParentDataSeries(getState().datasetViewer.selectedDataSeries),
      );

      dispatch({ type: ADD_DATA_SERIES_REQUEST, payload: dataSeriesName });

      return webApiClient
        .fetchDataSeries(dataset.id, [dataSeriesName])
        .then((response: DataSeries[]) => {
          if (response && response.length > 0) {
            dispatch({
              type: ADD_DATA_SERIES_SUCCESS,
              payload: response[0],
            });
          } else {
            throw new Error(`Adding of data series '${dataSeriesName}' failed`);
          }
        })
        .then(() => {
          // if the previously selected data series have a common "split by" condition, apply it also to the new one
          if (commonSplitConditionNameBeforeChange) {
            // retrieve the full condition object
            const commonSplitConditionBeforeChange: ColumnAnnotation =
              getState().datasetViewer.availableAnnotations.find(
                (annotation: ColumnAnnotation) => annotation.field === commonSplitConditionNameBeforeChange,
              );
            // and apply it
            dispatch(splitBySelectedCondition(null, commonSplitConditionBeforeChange));
          }
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: ADD_DATA_SERIES_FAILURE,
            alertTitle: "Adding of Data Series Failed",
          }),
        );
    }
  };
}

export function addGroupDataSeries(dataSeriesName: string, groupTableSelectionSpec: InputTableQuerySpec) {
  return (dispatch, getState, webApiClient: WebApiClient) => {
    const datasetState: IDatasetViewerState = getState().datasetViewer;
    const dataset: Dataset = datasetState.dataset;

    if (!datasetState.isLoading && dataset && datasetState.mode === DATA_VIEWER_MODE.GROUP) {
      dispatch({ type: ADD_DATA_SERIES_REQUEST, payload: dataSeriesName });

      return webApiClient
        .fetchGroupDataSeries([dataSeriesName], groupTableSelectionSpec)
        .then((response: DataSeries[]) => {
          if (response && response.length > 0) {
            dispatch({
              type: ADD_DATA_SERIES_SUCCESS,
              payload: response[0],
            });
          } else {
            throw new Error(`Adding of data series '${dataSeriesName}' failed`);
          }
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: ADD_DATA_SERIES_FAILURE,
            alertTitle: "Adding of Data Series Failed",
          }),
        );
    }
  };
}

export function searchDataSeries(searchTerm: string, datasetId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<DataSeries[]> => {
    const datasetState: IDatasetViewerState = getState().datasetViewer;

    if (datasetId && datasetState.mode === DATA_VIEWER_MODE.DATASET) {
      dispatch({ type: SEARCH_DATA_SERIES_REQUEST });

      return webApiClient
        .searchDataSeries(datasetId, searchTerm)
        .then((response: DataSeries[]) => {
          dispatch({
            type: SEARCH_DATA_SERIES_SUCCESS,
            payload: response || [],
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: SEARCH_DATA_SERIES_FAILURE,
            alertTitle: "Searching of Data Series Failed",
          }),
        );
    } else {
      return Promise.resolve([]);
    }
  };
}

export function searchGroupDataSeries(searchTerm: string, groupTableSelectionSpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<DataSeries[]> => {
    dispatch({ type: SEARCH_GROUP_DATA_SERIES_REQUEST, payload: groupTableSelectionSpec });

    return webApiClient
      .searchGroupDataSeries(searchTerm, groupTableSelectionSpec)
      .then((response: DataSeries[]) => {
        dispatch({
          type: SEARCH_GROUP_DATA_SERIES_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SEARCH_GROUP_DATA_SERIES_FAILURE,
          alertTitle: "Searching of Sample Group Data Series Failed",
        }),
      );
  };
}

export function getTableDataFromGroupId(groupId: number) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<ITableData> => {
    return dispatch(shouldLoadGroup(groupId)) // eventually get it from the cache if already fetched
      .then((group: Group) => {
        const entityType: string = group.type;
        const tableSpec: InputTableQuerySpec = createTableSpecForGroupParent(entityType, groupId, group.itemSubTypeId);

        // Make sure the created tableSpec generated from the groupID corresponds to this tableData slice
        // (the tableData key does not contain the group id). For more information see comments on SEL6-1436.
        const tableData: ITableData = getTableDataSlice(getState().tableData, tableSpec);
        if (!!tableData && sameTableSpec(tableSpec, tableData.tableQuerySpec)) {
          return tableData;
        }
        const fakeTableData: Partial<ITableData> = { tableQuerySpec: tableSpec };
        return fakeTableData;
      });
  };
}

export function exportAsGdaFile(payload: IDownloadGDAFileParams) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient.downloadFileAndWatchCompletion("/dataexport", payload);
    //return Promise.resolve({});
  };
}

export function chartLayoutStateChanged(chartLayoutModifications: ChartLayoutState) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const chartLayout: ChartLayoutState = { ...getState().datasetViewer.chartLayout, ...chartLayoutModifications };
    if (failingSettingsCombo(chartLayout, getState().datasetViewer.selectedAnnotations)) {
      chartLayoutModifications.isLogScale = false;
    }
    dispatch({
      type: CHART_LAYOUT_STATE_CHANGED,
      payload: chartLayoutModifications,
    });
  };
}

export function selectedConditionsChanged(selectedAnnotations: ColumnAnnotation[]) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    if (failingSettingsCombo(getState().datasetViewer.chartLayout, selectedAnnotations)) {
      dispatch({
        type: CHART_LAYOUT_STATE_CHANGED,
        payload: { isLogScale: false },
      });
    }
    dispatch({
      type: CHART_SELECTED_CONDITIONS_CHANGED,
      payload: selectedAnnotations,
    });
  };
}

// due to SEL5-1976, remove everything about this (also in ChartToolbar component)
// when https://github.com/recharts/recharts/issues/1107 is fixed
function failingSettingsCombo(chartLayout: ChartLayoutState, _selectedAnnotations: ColumnAnnotation[]) {
  return chartLayout.errorBars && chartLayout.isLogScale;
}

export function colorSettingsChanged(colorSettings: IColorSettings) {
  return {
    type: CHART_COLOR_SETTINGS_CHANGED,
    payload: colorSettings,
  };
}

export function selectedDataSeriesChanged(selectedDataSeries: SelectedDataSeries[]) {
  return {
    type: CHART_SELECTED_DATASERIES_CHANGED,
    payload: selectedDataSeries,
  };
}

export function splitBySelectedCondition(dataSeries: SelectedDataSeries, splitByCondition: ColumnAnnotation | null) {
  // This routine is called both if a specific data series is split, or if all data series are split.
  // In the latter case, no dataSeries is provided. The (previously) selected data series need to be taken form the dataViewer state then.

  return dataSeries
    ? {
        type: SPLIT_BY_CONDITION,
        payload: { dataSeries, splitByCondition },
      }
    : (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
        getParentDataSeries(getState().datasetViewer.selectedDataSeries).forEach((dataSeries) => {
          // Do not re-split when the split condition is the same as the one already applied to the data series
          // (to preserve e.g. color settings of the split tracks)
          if (dataSeries.splitByCondition !== splitByCondition?.field) {
            dispatch({
              type: SPLIT_BY_CONDITION,
              payload: { dataSeries, splitByCondition },
            });
          }
        });
      };
}

export function filteredConditionsChanged(conditionField: string, conditionFilter: IColumnAnnotationFilter) {
  return {
    type: CHART_FILTERED_CONDITIONS_CHANGED,
    payload: { conditionField, conditionFilter },
  };
}

export function setChartSelection(selection: string[]) {
  return {
    type: CHART_SELECTION_CHANGED,
    payload: selection,
  };
}

/**
 * Helper functions
 */

export const openDatasetInAnalyticalViewer = (datasetId: number) =>
  void pushRoute(`/visualizer/data/dataset/${datasetId}`);

function createAvailableColumnAnnotations(dataset: Dataset): ColumnAnnotation[] {
  const annotations: ColumnAnnotation[] = dataset.columnAnnotations.map((annotation: ColumnAnnotation) => {
    if (isDurationAnnotation(annotation)) {
      return { ...annotation, type: ANNOTATION_TYPE.DURATION };
    } else {
      return annotation;
    }
  });

  return [
    {
      ...SAMPLES_ANNOTATION,
      valueSet: dataset.columns.map((col: ICustomDatasetColumn) => col.name),
      values: dataset.columns.map((col: ICustomDatasetColumn) => ({ value: col.name })),
    },
    ...annotations,
  ];
}

function singleDataSeriesRequested(state: IDatasetViewerState, newSeriesName: string): IDatasetViewerState {
  const newRowColors: IDataSeriesColorMap = {};

  // Merge the new series into the existing list of series.
  //  - do not assign a color if the series is already part of the list.
  //  else
  //  - append the new series with a new, yet unused, color at the end of the list.
  //  preserve the order of existing series
  const pos = state.selectedDataSeries.findIndex((ds) => ds.name === newSeriesName);
  const selectedDataSeries: SelectedDataSeries[] = state.selectedDataSeries.slice(0);

  if (pos === -1) {
    const knownColor: string = state.colorSettings.rowColors[newSeriesName];
    const uniqueColor: string = knownColor
      ? knownColor
      : chooseUniqueColor(
          CHART_COLOR_SET,
          selectedDataSeries.slice(0).map((ds) => ds.color),
        );
    selectedDataSeries.push({
      ...initialSelectedDataSeries,
      isLoading: true,
      name: newSeriesName,
      color: uniqueColor,
    });
    newRowColors[newSeriesName] = uniqueColor;
  }

  return produce(state, (draft) => {
    draft.selectedDataSeries = selectedDataSeries;
    draft.colorSettings.rowColors = { ...draft.colorSettings.rowColors, ...newRowColors };
  });
}

function dataSeriesAdded(
  state: IDatasetViewerState,
  dataSeriesList: DataSeries[],
  removeMissing?: boolean,
): IDatasetViewerState {
  // Load a list of data series and merge/add them to the existing series.
  // Preserves the existing data series unless it is the reloaded one.

  // We store the new data series keyed by their name in a map
  const newDataSeriesMap: IDataSeriesMap = { ...state.dataSeriesMap, ...keyBy(dataSeriesList.slice(0), "name") };
  // we update the isLoading flag
  const newSelectedDataSeries: SelectedDataSeries[] = [];
  state.selectedDataSeries.forEach((selectedDataseries: SelectedDataSeries) => {
    if (dataSeriesList.find((ds: DataSeries) => ds.name === selectedDataseries.name)) {
      newSelectedDataSeries.push({ ...selectedDataseries, isLoading: false });
    } else if (!removeMissing) {
      newSelectedDataSeries.push(selectedDataseries);
    }
  });

  return {
    ...state,
    dataSeriesMap: newDataSeriesMap,
    selectedDataSeries: newSelectedDataSeries,
  };
}

function multipleDataSeriesRequested(state: IDatasetViewerState, dataSeriesNames: string[]): IDatasetViewerState {
  const newRowColors: IDataSeriesColorMap = {};
  const usedColors: string[] = [];

  // We create a new set of selected data series based on the previous configuration: we keep the same config
  const selectedDataSeries: SelectedDataSeries[] = dataSeriesNames.map((dataSeriesName: string) => {
    const newColor = chooseUniqueColor(CHART_COLOR_SET, usedColors);
    usedColors.push(newColor);
    newRowColors[dataSeriesName] = newColor;
    return { ...initialSelectedDataSeries, isLoading: true, name: dataSeriesName, color: newColor };
  });

  return produce(state, (draft) => {
    draft.selectedDataSeries = selectedDataSeries;
    draft.colorSettings.rowColors = { ...draft.colorSettings.rowColors, ...newRowColors };
  });
}

export function getStringValue(valuesList: StringMapEntry[], key: string): string {
  const entry: StringMapEntry = valuesList.find((e: StringMapEntry) => e.key === key);
  return entry ? entry.value : undefined;
}

export function isDurationAnnotation(annotation: ColumnAnnotation) {
  if (annotation.type !== ANNOTATION_TYPE.CATEGORICAL) {
    return false;
  }

  const valueSet: string[] = annotation.valueSet;
  // check if all values can be parsed as duration
  return valueSet.filter((value: string) => !isNaN(parseDurationAsSeconds(value))).length === valueSet.length;
}

export function isTimeSeriesEnabled(selectedAnnotations: ColumnAnnotation[]): boolean {
  if (selectedAnnotations.length === 1) {
    const type: string = selectedAnnotations[0].type;
    return type === ANNOTATION_TYPE.NUMERICAL || type === ANNOTATION_TYPE.DURATION;
  }
  return false;
}

function updateTimeSeriesLayoutOptionFromSelectedAnnotations(
  annotations: ColumnAnnotation[],
  availableAnnotations: ColumnAnnotation[],
  chartLayout: ChartLayoutState,
): ChartLayoutState {
  const selectedAnnotations: ColumnAnnotation[] = annotations.map((a: ColumnAnnotation) => {
    return availableAnnotations.find((ca: ColumnAnnotation) => ca.name === a.name);
  });
  // we disable the time series in case it is enabled
  return isTimeSeriesEnabled(selectedAnnotations) ? chartLayout : { ...chartLayout, timeSeries: false };
}

export function orderDataSeriesConsideringSplitSeries(dataSeries: SelectedDataSeries[]) {
  const orderedDataSeries: SelectedDataSeries[] = [];
  dataSeries
    .filter((ds: SelectedDataSeries) => !ds.splitParent)
    .forEach((ds: SelectedDataSeries) => {
      if (ds.splitByCondition && !ds.splitParent) {
        orderedDataSeries.push(...dataSeries.filter((sds: SelectedDataSeries) => sds.splitParent === ds.name));
      } else {
        orderedDataSeries.push(ds);
      }
    });
  return orderedDataSeries;
}

function isExcludeColumnByAnnotationFilter(
  filteredAnnotations: IColumnAnnotationFilterMap,
  annotation: StringMapEntry,
): boolean {
  const columnAnnotationFilter: IColumnAnnotationFilter = filteredAnnotations[annotation.key];
  if (columnAnnotationFilter) {
    if (columnAnnotationFilter.categoricalValues?.indexOf(annotation.value) > -1) {
      return true;
    }
    if (
      columnAnnotationFilter.minNumericalValue &&
      Number(annotation.value) < columnAnnotationFilter.minNumericalValue
    ) {
      return true;
    }
    if (
      columnAnnotationFilter.maxNumericalValue &&
      Number(annotation.value) > columnAnnotationFilter.maxNumericalValue
    ) {
      return true;
    }
  }
  return false;
}

export function countNumberOfActiveFilters(filteredOutAnnotations: IColumnAnnotationFilterMap): number {
  return Object.keys(filteredOutAnnotations).reduce((increment: number, columnAnnotation: string) => {
    const columnAnnotationFilter: IColumnAnnotationFilter = filteredOutAnnotations[columnAnnotation];
    if (
      (columnAnnotationFilter.categoricalValues && columnAnnotationFilter.categoricalValues.length > 0) ||
      columnAnnotationFilter.minNumericalValue != null || // make sure that value 0 is considered active filter
      columnAnnotationFilter.maxNumericalValue != null // make sure that value 0 is considered active filter
    ) {
      return ++increment;
    }
    return increment;
  }, 0);
}

export function splitPaneLayoutStateChanged(splitPaneLayout: SplitPaneLayoutState) {
  return {
    type: SPLIT_PANE_LAYOUT_STATE_CHANGED,
    payload: splitPaneLayout,
  };
}

export function chartTableSelectionModelUpdated(selectionModel: ISelectionModel) {
  return {
    type: CHART_TABLE_SELECTION_CHANGED,
    payload: {
      selectionModel: cloneSelectionModel(selectionModel),
    },
  };
}

export function setChartData(data: ISetChartData) {
  return {
    type: SET_CHART_DATA,
    payload: data,
  };
}

export function toggleDisplayOfDataSeriesWithoutData() {
  return {
    type: TOGGLE_DISPLAY_OF_DATA_SERIES_WITHOUT_DATA,
  };
}

export function recalculateDataSeriesColors() {
  return {
    type: RECALCULATE_DATA_SERIES_COLORS,
  };
}

/****** selectors / memoizers ******/

// make sure that the list of visible selected dataseries is not mutated if nothing changes (so that the below getChartData memoizer does only recalculate when necessary)
export const getOrderedVisibleSelectedDataseries = memoize(
  (selectedDataSeries: SelectedDataSeries[]): SelectedDataSeries[] => selectedDataSeries,
  (args) => JSON.stringify(args), // allows simple cache id resolution based on content rather than on the reference (each time the array is new because of the array.filter)
);

// recalculate the chart data only when the input props have changed, otherwise it might be refreshed too often (e.g. when chart is resized...)
/*
export const getChartData =
  memoizee((selectedDataSeries: Array<ISelectedDataSeries>, selectedAnnotations: Array<IColumnAnnotation>, dataSeriesMap: IDataSeriesMap, columns: Array<ICustomDatasetColumn>, layout: IChartLayoutState): Array<IConditionCombination> =>
    calculateChartData({ selectedDataSeries, selectedAnnotations, dataSeriesMap, columns, layout } as IDatasetChartProps),
    { max: 1,                   // keep a single version in cache
  });
  */

export function getParentDataSeries(dataSeries: SelectedDataSeries[]) {
  return dataSeries.filter((ds) => !ds.splitParent);
}

// refactor?
export function getCommonSplitByCondition(dataSeries: SelectedDataSeries[]) {
  const splitConditions = dataSeries.map((ds) => ds.splitByCondition);
  return uniq(splitConditions).length > 1 ? null : splitConditions.length > 0 ? splitConditions[0] : null;
}

export function shouldShowDataSeriesInLegend(dataSeries: SelectedDataSeries) {
  return (_dispatch, getState, _webApiClient: WebApiClient) => {
    return isDataSeriesVisible(dataSeries, getState().datasetViewer);
  };
}

function isDataSeriesVisible(dataSeries: SelectedDataSeries, datasetViewerState: IDatasetViewerState) {
  const seriesKey: string | null = dataSeries?.name && (getDataSeriesKey(dataSeries.name) as string);
  const allDataSeriesKeysWithData = datasetViewerState.allDataSeriesKeysWithData;
  const hideDataSeriesWithoutData = datasetViewerState.hideEmptyDataSeries;
  // if a data series is hidden by eye symbol, still keep it in the list
  // (because we should not hide the "eye" button)
  if (dataSeries.hidden) {
    return true;
  }
  return !!allDataSeriesKeysWithData.find((k: string) => k === seriesKey) || !hideDataSeriesWithoutData;
}
